import { MenuListParams } from "../../../api/services/client/models";
import { IdNameList } from "../../../api/services/common/models";
import { ChipOptionsDataType } from "../../../components";
import { LabelValue } from "../../../helpers/types/common";

export const ParticipantsSearchFormKey = {
  searchType: "searchType",
  resetForm: "resetForm",

  // MyCaseLoads & All Participants Common
  participantFirstName: "participantFirstName",
  participantFirstNameFilter: "participantFirstNameFilter",
  participantLastName: "participantLastName",
  participantLastNameFilter: "participantLastNameFilter",
  keyNumber: "keyNumber",
  keyNumberFilter: "keyNumberFilter",
  participantEmail: "participantEmail",
  participantEmailFilter: "participantEmailFilter",
  mobileISOCode: "mobileISOCode",
  mobileCountryCode: "mobileCountryCode",
  participantMobileNumber: "participantMobileNumber",
  clientName: "clientName",
  clientNameFilter: "clientNameFilter",
  ageing: "ageing",

  //All Participants
  coachType: "coachType",
  coachIds: "coachIds",
  participantStatus: "participantStatus",
} as const;

export const SEARCH_TYPE_KEY = {
  myCaseLoads: "myCaseLoads",
  allParticipants: "allParticipants",
};

export interface ParticipantsSearchFormProps {
  searchType: string;
  resetForm?: boolean;
  // MyCaseLoads
  participantFirstName?: string;
  participantFirstNameFilter?: string;
  participantLastName?: string;
  participantLastNameFilter?: string;
  keyNumber?: string;
  keyNumberFilter?: string;
  participantEmail?: string;
  participantEmailFilter?: string;
  mobileISOCode?: string;
  mobileCountryCode?: string;
  participantMobileNumber?: string;
  clientName?: string;
  clientNameFilter?: string;
  ageing?: string;
  
  // All Participants
  coachType?: string;
  coachIds?: string[];
  participantStatus?: string[];
}

export interface CoachDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  coachTypes: IdNameList[];
}

export interface ParticipantSearchResultRowValue {
  id: string;
  participantName: string;
  keyNumber: string;
  nextView: IdNameList;
  nextViewName: string;
  nextSessionEvent: string;
  aging: string;
  agingValue: string;
  coaches: CoachDetails[];
  coachNamesArray: ChipOptionsDataType[];
  companyName: string;
  programStatus: string;
  participantEmail?: string;
  participantMobileNumber?: string;
  projectManager?: string;
  country: string;
  countryName?: string;
  programStartDate?: string;
  programEndDate?: string;
  [key: string]: any;
}

export interface ParticipantSearchResultForm
  extends Omit<ParticipantSearchResultRowValue, "coachNames" | "countryName"> {
  coachName: string;
  countryIds: string[];
}

export const SearchParticipantsResultFormKey = {
  participantName: "participantName",
  keyNumber: "keyNumber",
  nextView: "nextView",
  nextViewName: "nextViewName",
  aging: "aging",
  agingValue: "agingValue",
  nextSessionEvent: "nextSessionEvent",
  coaches: "coaches",
  companyName: "companyName",
  programStatus: "programStatus",
  participantEmail: "participantEmail",
  participantMobileNumber: "participantMobileNumber",
  projectManager: "projectManager",
  countryName: "countryName",
  countryIds: "countryIds",
  programStartDate: "programStartDate",
  programEndDate: "programEndDate",
};

export interface UniqueParticipantSearchResultFiltersTypes {
  programStatus: LabelValue[];
}

export interface ManageParticipantsProgramTabResponse {
  keyNumber?: string;
  lineOfBusinessId?: string;
  lineOfBusiness?: string;
  opportunityId?: string;
  clientId?: string;
  clientName?: string;
  clientCode?: string;
  deliveryCountry?: string;
  deliveryCountryName?: string;
  registrationCode?: string;
  registrationStatus?: string;
  registrationEmailId?: string;
  businessUnit?: string;
  costCenter?: string;
  programClass?: string;
  programTemplateId?: string;
  salesOfficeId?: string;
  programName?: string;
  solutionOfferingId?: string;
  programClassId?: string;
  billingContactId?: string;
  billingFirstName?: string;
  billingLastName?: string;
  billingEmail?: string;
  hrContactFirstName?: string;
  hrContactLastName?: string;
  hrContactId?: string;
  programTemplate?: string;
  billingCountryId?: string;
  billingOffice?: string;
  alternateDisplayName?: string;
  salesOffice?: string;
  programStatus?: string;
  solutionOffering?: string;
  deliveryOffice?: string;
  billingContact?: string;
  hrContact?: string;
  clientRelationShipOwner?: string;
  clientRelationShipOwnerId?: string;
  projectManager?: string;
  projectManagerId?: string;
  socialProgram?: boolean;
  clientRelationshipOwnerFirstName?: string;
  clientRelationshipOwnerLastName?: string;
  projectManagerFirstName?: string;
  projectManagerLastName?: string;
  poNumber?: string;
  projectCoordinatorFirstName?: string;
  projectCoordinatorLastName?: string;
  versions?: MenuListParams[];
  programEndDate?: string;
}

export type CoachMetricsCardTypes =
  | "caseload_vs_engagement"
  | "average_time_spent"
  | "coach_activities"
  | "participants_landed";

export type ParticipantDetailsCardTypes =
  | "activities"
  | "events"
  | "assessments";

export const ManageParticipantsSessionAndEventsFilterFormKeys = {
  type: "type",
  period: "period",
  category: "category",
} as const;

export const COACH_METRICS_DASHBOARD_PERIOD_VALUES = {
  LAST_6_MONTHS: "Period.Last6Months",
  YEAR_TILL_DATE: "Period.YearTillDate",
};
export const AGEING_FILTER_KEYS = {
  ALL: "Ageing.All",
  NEW_JOINERS: "Ageing.NewJoiners",
  NEARING_CLOSURE: "Ageing.NearingClosure",
};

export const TYPE_FILTER_KEYS = {
  UPCOMING_SESSIONS_AND_EVENTS:
    "SessionsAndEventsType.UpcomingSessionsAndEvents",
  PAST_SESSIONS_AND_EVENTS: "SessionsAndEventsType.PastSessionsAndEvents",
};

export const UPCOMING_PERIOD_FILTER_KEYS = {
  NEXT_7_DAYS: "Period.Next7Days",
  NEXT_14_DAYS: "Period.Next14Days",
  NEXT_1_MONTH: "Period.Next1Month",
  NEXT_3_MONTHS: "Period.Next3Months",
};

export const PAST_PERIOD_FILTER_KEYS = {
  PAST_7_DAYS: "Period.Past7Days",
  PAST_14_DAYS: "Period.Past14Days",
  PAST_1_MONTH: "Period.Past1Month",
  PAST_3_MONTHS: "Period.Past3Months",
  PAST_1_YEAR: "Period.Past1Year",
  PAST_3_YEARS: "Period.Past3Years",
};

export const CATEGORY_FILTER_KEYS = {
  ALL: "SessionsAndEventsCategory.All",
  COACHING_SESSIONS: "SessionsAndEventsCategory.CoachingSessions",
  LIVE_WEBINAR_EVENTS: "SessionsAndEventsCategory.LiveWebinarEvents",
};

export const MY_MEETINGS_TYPE_FILTER_KEYS = {
  UPCOMING_SESSION: "SessionsAndEventsType.UpcomingSessions",
  PAST_SESSION: "SessionsAndEventsType.PastSessions",
};

export const MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS = {
  TODAY: "Period.Today",
  TOMORROW: "Period.Tommorow",
  NEXT_7_DAYS: "Period.Next7Days",
  NEXT_30_DAYS: "Period.Next30Days",
  NEXT_3_MONTHS: "Period.Next3Months",
};

export const MY_MEETINGS_PAST_PERIOD_FILTER_KEYS = {
  TODAY: "Period.Today",
  YESTERDAY: "Period.Yesterday",
  PAST_7_DAYS: "Period.Past7Days",
  PAST_30_DAYS: "Period.Past30Days",
  PAST_3_MONTHS: "Period.Past3Months",
};

export const MyMeetingsFilterFormKeys = {
  type: "type",
  period: "period",
} as const;

export interface ParticipantSessionAndEventsResponse {
  id: string;
  title: string;
  sessionDate: string;
  isExpandable: boolean;
  duration: string;
  createdDate: string;
  createdBy: string;
  categoryId: string;
  mode: string;
  organizer: IdNameList;
  instructor: IdNameList;
  notes: string;
}

export const CPParticipantsResultFormKey = {
  participantName: "participantName",
  programTemplateName: "programTemplateName",
  programStatus: "programStatus",
  nextContactDate: "nextContactDate",
  upcomingSession: "upcomingSession",
  programEndDate: "programEndDate",
  coaches: "coaches",
  participantEmail: "participantEmail",
  participantMobileNumber: "participantMobileNumber",
  projectManager: "projectManager",
  countryName: "countryName",
  countryIds: "countryIds",
  programStartDate: "programStartDate",
};

export interface CPParticipantsResultRowValue {
  id: string;
  participantName: string;
  programTemplateName: string;
  nextContactDate: string;
  upcomingSession: string;
  coaches: CoachDetails[];
  coachNamesArray: ChipOptionsDataType[];
  programStatus: string;
  participantEmail?: string;
  participantMobileNumber?: string;
  projectManager?: string;
  country: string;
  countryName?: string;
  programStartDate?: string;
  programEndDate?: string;
  [key: string]: any;
}

export interface CPParticipantsResultForm
  extends Omit<CPParticipantsResultRowValue, "countryName"> {
  coachName: string;
  countryIds: string[];
}

export const MY_CASELOADS_TABS_KEYS = {
  NEW_JOINERS: "NewJoiners",
  NEARING_CLOSURE: "NearingClosure",
  ALL: "All",
};

export interface ParticipantLandedResultRowValue {
  id: string;
  participantName: string;
  programTemplateName: string;
  participantEmail?: string;
  participantMobileNumber?: string;
  projectManager?: string;
  country: string;
  countryName?: string;
  programStartDate?: string;
  programEndDate?: string;
  [key: string]: any;
}

export const ParticipantLandedResultFormKey = {
  participantName: "participantName",
  programTemplateName: "programTemplateName",
  programEndDate: "programEndDate",
  participantEmail: "participantEmail",
  participantMobileNumber: "participantMobileNumber",
  projectManager: "projectManager",
  countryName: "countryName",
  countryIds: "countryIds",
  programStartDate: "programStartDate",
};

export interface ParticipantLandedResultForm
  extends Omit<ParticipantLandedResultRowValue, "countryName"> {
  countryIds: string[];
}

export const CaseLoadsVsEngagementsResultFormKey = {
  participantName: "participantName",
  programTemplateName: "programTemplateName",
  programStatus: "programStatus",
  nextContactDate: "nextContactDate",
  upcomingSession: "upcomingSession",
  programEndDate: "programEndDate",
  coaches: "coaches",
  participantEmail: "participantEmail",
  participantMobileNumber: "participantMobileNumber",
  projectManager: "projectManager",
  countryName: "countryName",
  countryIds: "countryIds",
  programStartDate: "programStartDate",
};

export interface CaseLoadsVsEngagementsResultRowValue {
  id: string;
  participantName: string;
  programTemplateName: string;
  nextContactDate: string;
  upcomingSession: string;
  coaches: CoachDetails[];
  coachNamesArray: ChipOptionsDataType[];
  programStatus: string;
  participantEmail?: string;
  participantMobileNumber?: string;
  projectManager?: string;
  country: string;
  countryName?: string;
  programStartDate?: string;
  programEndDate?: string;
  [key: string]: any;
}

export interface CaseLoadsVsEngagementsResultForm
  extends Omit<CaseLoadsVsEngagementsResultRowValue, "countryName"> {
  coachName: string;
  countryIds: string[];
}
